import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import * as selectionApi from '@/services/selection/workspaces';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';
import { Organization, WorkspaceMember } from '@/types/workspace';
import { useSelection } from '@/selection/SelectionProvider';

// Minimal workspace shape used by selection UIs
export type MinimalWorkspace = {
  id: string;
  name: string;
  organization: Organization;
  userId?: string | null;
  role?: string | null;
  authUserId?: string | null;
  raw?: any;
};

export const useWorkspaces = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Compute authUserId synchronously, prefer current user.id and fallback to persisted store.
  const computeAuthUserId = () => {
    // Prefer fresh in-memory user id first
    if (user?.id) return user.id;
    // Then try sessionStorage persisted userData
    try {
      const store = (typeof window !== 'undefined' ? sessionStorage.getItem('userData') : null) || null;
      if (store) {
        const parsed = JSON.parse(store || '{}');
        return parsed?.authUserId || parsed?.AuthUserId || parsed?.userId || parsed?.id || null;
      }
    } catch (_) {}
    // Finally try in-memory authStore as a fallback
    try {
      const s = require('@/services/authStore').getAuthStore();
      if (s?.userData) return s.userData.authUserId || s.userData.id || null;
    } catch (e) {
      // ignore
    }
    return null;
  };

  const authUserIdSync = computeAuthUserId();
  console.debug('[useWorkspaces] authUserIdSync', { authUserIdSync, userId: user?.id });

  // Only fetch on the selection screen. Prevents any calls on '/', '/auth', '/dashboard', etc.
  const { pathname } = useLocation();
  const selection = useSelection();
  const isSelectionFlow = Boolean(selection?.active) || pathname.startsWith('/select-workspace');

  const workspacesQuery = useQuery<MinimalWorkspace[]>({
    queryKey: ['workspaces', authUserIdSync],
    queryFn: async () => {
      console.debug('[useWorkspaces] queryFn start', { userId: user?.id, authUserIdSync });

      // Do NOT call verifySession here. We want to avoid triggering /auth/me and
      // subsequent refresh flows that can block other requests. The backend can
      // infer identity from cookies or existing token headers.

  const authUserId = computeAuthUserId();
  console.debug('[useWorkspaces] using authUserId for request', { authUserId });

  // Always call the API; if authUserId is missing, backend should infer from token
  const items = await selectionApi.getUserWorkspaces(authUserId ?? undefined);
      const mapped: MinimalWorkspace[] = items.map((it: any) => ({
        id: String(it.workspaceId || it.workspace_id || it.id),
        name: it.workspaceName || it.workspace_name || it.name || 'Workspace',
        organization: { name: it.organizationName || it.organization?.name } as any,
        partner_name: it.partnerName || it.partner_name || undefined,
        authUserId: String(it.authUserId || it.AuthUserId || '' ) || null,
        userId: String(it.userId || it.user_id || '' ) || null,
        role: it.role || null,
        raw: it,
      }));

      return mapped;
    },
  // Always allow fetch on selection flow; backend can infer identity from token if id is missing
  enabled: isSelectionFlow,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Default workspace auto-selection is removed to avoid accidental toasts and extra logic.

  useEffect(() => {
    if (workspacesQuery.isError && workspacesQuery.error) {
      toast({
        title: 'Workspace Loading Error',
        description: 'Failed to load workspaces. Please try refreshing the page.',
        variant: 'destructive',
      });
    }
  }, [workspacesQuery.isError, workspacesQuery.error, toast]);

  // No default workspace toast; selection will be explicit on the selection page.

  return {
    workspaces: (workspacesQuery.data || []) as MinimalWorkspace[],
    defaultWorkspaceId: undefined,
    isLoading: workspacesQuery.isLoading,
    error: workspacesQuery.error,
    // Invalidate all workspace queries
    invalidate: () => queryClient.invalidateQueries({ queryKey: ['workspaces'] }),
  };
};

export const useWorkspaceMembers = (workspaceId: string) => {
  // Placeholder implementation until a workspace-members API is added.
  return {
    members: [] as WorkspaceMember[],
    isLoading: false,
    error: null,
    inviteMember: () => {},
    isInviting: false,
  };
};
